import api from '../../../lib/api';
import type {
  ApplyPlanInput,
  CreateOrganizationProductInput,
  DeleteOrganizationProductInput,
  GeneratePlanInput,
  ImportOrganizationProductsInput,
  OnboardingOutputLanguage,
  OnboardingState,
  OrganizationProduct,
  OrganizationProfile,
  PatchStateInput,
  ProductImportResult,
  ProductTemplateLocale,
  SaveProfileInput,
  UpdateOrganizationProductInput,
} from '../types';

export const onboardingApi = {
  getState: (organizationId: string, outputLanguage?: OnboardingOutputLanguage) =>
    api
      .get<OnboardingState>('/onboarding/state', { params: { organizationId, outputLanguage } })
      .then((res) => res.data),

  getProfile: (organizationId: string, outputLanguage?: OnboardingOutputLanguage) =>
    api
      .get<OrganizationProfile>('/onboarding/profile', {
        params: { organizationId, outputLanguage },
      })
      .then((res) => res.data),

  saveProfile: (input: SaveProfileInput) =>
    api.post<OnboardingState>('/onboarding/profile', input).then((res) => res.data),

  // Per-request override only: inference forces a tool call bounded by a
  // 50s server-side single-attempt timeout, but a legitimately slow (not
  // hung) generation can still exceed the global 30s axios default and get
  // aborted client-side before the backend's own fallback ever gets a
  // chance to fire. Do NOT change the global default in lib/api.ts for this.
  //
  // The budget covers TWO server-side phases now: up to 50s of inference, then
  // a translation pass to render the response in the org's language. At 60s a
  // slow-but-valid generation could be aborted client-side AFTER the plan was
  // already persisted, and the retry would generate a second one.
  generatePlan: (input: GeneratePlanInput) =>
    api
      .post<OnboardingState>('/onboarding/generate', input, { timeout: 90000 })
      .then((res) => res.data),

  applyPlan: (input: ApplyPlanInput) =>
    api.post<OnboardingState>('/onboarding/apply', input).then((res) => res.data),

  patchState: (input: PatchStateInput) =>
    api.patch<OnboardingState>('/onboarding/state', input).then((res) => res.data),

  listProducts: (organizationId: string) =>
    api
      .get<OrganizationProduct[]>('/onboarding/products', { params: { organizationId } })
      .then((res) => res.data),

  createProduct: (input: CreateOrganizationProductInput) =>
    api.post<OrganizationProduct>('/onboarding/products', input).then((res) => res.data),

  updateProduct: ({ productId, ...body }: UpdateOrganizationProductInput) =>
    api
      .patch<OrganizationProduct>(`/onboarding/products/${productId}`, body)
      .then((res) => res.data),

  // organizationId travels as a query param because DELETE carries no body,
  // and OrgAccessGuard needs it to resolve the tenant.
  deleteProduct: ({ organizationId, productId }: DeleteOrganizationProductInput) =>
    api
      .delete<{ id: string }>(`/onboarding/products/${productId}`, { params: { organizationId } })
      .then((res) => res.data),

  getProductLinkedLeads: (organizationId: string, productId: string) =>
    api
      .get<{ linkedLeads: number }>(`/onboarding/products/${productId}/linked-leads`, {
        params: { organizationId },
      })
      .then((res) => res.data.linkedLeads),

  downloadProductsTemplate: (locale: ProductTemplateLocale) =>
    api
      .get<Blob>('/onboarding/products/template', { responseType: 'blob', params: { locale } })
      .then((res) => res.data),

  // organizationId travels as a query param: the backend guard runs before the
  // multipart body is parsed, so it cannot be read from the form data.
  importProducts: ({ organizationId, file }: ImportOrganizationProductsInput) =>
    api
      .post<ProductImportResult>('/onboarding/products/import', file, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { organizationId },
      })
      .then((res) => res.data),
};
